// @ts-strict-ignore
import { Component, HostBinding, OnInit } from '@angular/core';
import { Subject } from 'rxjs/internal/Subject';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ProjectGroupLoadingService } from '@project/components/project/service/project-group-loading.service';
import { FriendSharedInfo } from '@project/components/permissions/friend-shared-info';
import { Dictionary } from 'typescript-collections';
import { SharedUserRightVO } from '../../model/vo/shared-user-right.vo';
import { ProjectGroupVO } from '@project/model/vo/project-group.vo';
import { ArrayUtils } from '@shared/util/array-utils';
import { ProjectHeadVO, ProjectVO } from '@project/model/vo/project.vo';
import { CloneUtils } from '@shared/util/clone-utils';
import { ProjectGroupUtils } from '@project/components/project/util/project-group-utils';
import { UserModel } from '../../model/user.model';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { UserUtils } from '../../service/user-utils';
import { StringUtils } from '@shared/util/string-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { Log } from '@shared/debug/log';
import { SvgIconName } from '@shared/svg-icon/svg-icon-name';

interface PermissionsUserItem {
  userName: string;
  navigationChain: ProjectGroupVO[];
  configurationGroups: ProjectGroupVO[];
}

/**
 * An overview of all permissions of a user role
 */
@Component({
  standalone: false,
  selector: 'permissions-user',
  templateUrl: './permissions-user.component.html',
})
export class PermissionsUserComponent implements OnInit {
  public readonly PROJECT_FOLDER_TYPE: number = ProjectGroupUtils.PROJECT;

  public onClose: Subject<boolean>;

  @HostBinding(`class.d-flex`) displayFlexClass = true;
  @HostBinding(`class.flex-column`) flexDirectionClass = true;

  private _preselectedFriendSharedInfo: FriendSharedInfo;
  private _preselectedProjectGroupId: number;

  private _changedRootIds: number[] = [];
  private _groupIdToSharedInfos: Dictionary<number, SharedUserRightVO> = new Dictionary<number, SharedUserRightVO>();
  private _projectIdToSharedInfos: Dictionary<number, SharedUserRightVO> = new Dictionary<number, SharedUserRightVO>();

  private _groupIdToChildGroupIdsForFiltering: Dictionary<number, number[]> = new Dictionary<number, number[]>();
  private _groupIdToProjectIdsForFiltering: Dictionary<number, number[]> = new Dictionary<number, number[]>();

  public ownerProjectGroups: ProjectGroupVO[];

  public viewItems: PermissionsUserItem[];

  public get changedRootIds(): number[] {
    return this._changedRootIds;
  }

  public set changedRootIds(value: number[]) {
    this._changedRootIds = value;
  }

  public get groupIdToSharedInfos(): Dictionary<number, SharedUserRightVO> {
    return this._groupIdToSharedInfos;
  }

  public set groupIdToSharedInfos(value: Dictionary<number, SharedUserRightVO>) {
    this._groupIdToSharedInfos = value;
  }

  public get projectIdToSharedInfos(): Dictionary<number, SharedUserRightVO> {
    return this._projectIdToSharedInfos;
  }

  public set projectIdToSharedInfos(value: Dictionary<number, SharedUserRightVO>) {
    this._projectIdToSharedInfos = value;
  }

  public get preselectedFriendSharedInfo(): FriendSharedInfo {
    return this._preselectedFriendSharedInfo;
  }

  public set preselectedFriendSharedInfo(value: FriendSharedInfo) {
    this._preselectedFriendSharedInfo = value;
  }

  public get preselectedProjectGroupId(): number {
    return this._preselectedProjectGroupId;
  }

  public set preselectedProjectGroupId(value: number) {
    this._preselectedProjectGroupId = value;
  }

  public get groupIdToChildGroupIdsForFiltering(): Dictionary<number, number[]> {
    return this._groupIdToChildGroupIdsForFiltering;
  }

  public set groupIdToChildGroupIdsForFiltering(value: Dictionary<number, number[]>) {
    this._groupIdToChildGroupIdsForFiltering = value;
  }

  public get groupIdToProjectIdsForFiltering(): Dictionary<number, number[]> {
    return this._groupIdToProjectIdsForFiltering;
  }

  public set groupIdToProjectIdsForFiltering(value: Dictionary<number, number[]>) {
    this._groupIdToProjectIdsForFiltering = value;
  }

  constructor(private bsModalRef: BsModalRef, private userModel: UserModel, protected projectGroupLoadingService: ProjectGroupLoadingService) {}

  public get userID(): number {
    return this.userModel.user?.id;
  }

  public ngOnInit(): void {
    this.onClose = new Subject();

    this.loadBaseGroups();
  }

  public onCancel(): void {
    this.onClose.next(null);
    this.bsModalRef.hide();
  }

  public projectGroupsToViewItems(projectGroups: ProjectGroupVO[], navigationChain: ProjectGroupVO[] = []): void {
    const configurationGroups: ProjectGroupVO[] = projectGroups.filter((g: ProjectGroupVO) =>
      ProjectGroupUtils.isConfigurationGroupType(g.groupType)
    );
    if (ArrayUtils.isNotEmpty(configurationGroups)) {
      const userNameOfConfigurationGroups: string = configurationGroups[0].groupOwner.username;
      configurationGroups.forEach((g: ProjectGroupVO) =>
        g.projects.sort(ProjectUtils.projectSortByNameFunction)
      );
      this.viewItems.push({
        userName: userNameOfConfigurationGroups,
        navigationChain: navigationChain,
        configurationGroups: configurationGroups,
      });
    }
    const groups: ProjectGroupVO[] = projectGroups.filter((g: ProjectGroupVO) => !ProjectGroupUtils.isConfigurationGroupType(g.groupType));
    for (const group of groups) {
      if (ArrayUtils.isNotEmpty(group.groups)) {
        group.groups.sort((a: ProjectGroupVO, b: ProjectGroupVO) =>
          ProjectGroupUtils.projectGroupSortFunction(a, b, this.preselectedFriendSharedInfo.userId)
        );
        this.projectGroupsToViewItems(group.groups, navigationChain.concat(group));
      } else {
        const userNameOfGroup: string = group.groupOwner.username;
        this.viewItems.push({
          userName: userNameOfGroup,
          navigationChain: navigationChain.concat(group),
          configurationGroups: [],
        });
      }
    }
  }

  private async loadBaseGroups(): Promise<void> {
    if (this._preselectedFriendSharedInfo) {
      try {
        const projectGroups = await this.projectGroupLoadingService.loadFilteredBaseGroups(this.preselectedFriendSharedInfo.userId, []);
        this.loadFilteredBaseProjectGroupsSuccessHandler(projectGroups);
      } catch (error) {
        Log.error(error);
      }
    }
  }

  private loadFilteredBaseProjectGroupsSuccessHandler(projectGroups: ProjectGroupVO[]) {
    const locallyChangedGroupNodes = this.getLocallyChangedGroupNodes(this.changedRootIds);
    this.mergeGroupTrees(projectGroups, locallyChangedGroupNodes);
    const filteredBaseGroups: ProjectGroupVO[] = [];
    this.updateProjectGroupsByChangedSharedInfo(projectGroups, filteredBaseGroups);
    this.viewItems = [];
    filteredBaseGroups.sort((a, b) => ProjectGroupUtils.projectGroupSortFunction(a, b, this.preselectedFriendSharedInfo.userId));
    this.projectGroupsToViewItems(filteredBaseGroups);
    this.viewItems.sort((a, b) => this.sortSharedProjectGroupsByUserName(a, b));
  };

  private sortSharedProjectGroupsByUserName(a: PermissionsUserItem, b: PermissionsUserItem): number {
    const userName: string = this.userModel.user.login;
    let elementA: number;
    let elementB: number;

    elementA = a.userName === userName ? -1 : 1;
    elementB = b.userName === userName ? -1 : 1;

    if (elementA !== elementB) {
      return NumberUtils.numericCompare(elementA, elementB);
    }

    const stringCompareResult: number = StringUtils.stringCompare(a.userName, b.userName);
    return stringCompareResult;
  }

  public isUserHasPermissionToReadSharedInfo() {
    return UserUtils.hasReadSharedInfoFromAllUsersPermission(this.userModel.user);
  }

  private mergeGroupTrees(target: ProjectGroupVO[], source: ProjectGroupVO[]): void {
    if (ArrayUtils.isEmpty(source)) {
      return;
    }
    if (ArrayUtils.isEmpty(target)) {
      ArrayUtils.mergeArrays(target, source);
      return;
    }
    for (const sourceGroup of source) {
      const targetGroup: ProjectGroupVO = target.find((g) => g.id === sourceGroup.id);
      if (targetGroup) {
        targetGroup.childContentLoaded = sourceGroup.childContentLoaded;
        this.mergeProjects(targetGroup.projects, sourceGroup.projects);
        this.mergeGroupTrees(targetGroup.groups, sourceGroup.groups);
      } else {
        target.push(sourceGroup);
      }
    }
  }

  private findGroupById(groupId: Number, source: ProjectGroupVO[]): ProjectGroupVO {
    for (const group of source) {
      if (group.id === groupId) {
        return group;
      }
      if (ArrayUtils.isNotEmpty(group.groups)) {
        const projectGroup: ProjectGroupVO = this.findGroupById(groupId, group.groups);
        if (projectGroup) {
          return projectGroup;
        }
      }
    }
    return null;
  }

  private mergeProjects(target: ProjectHeadVO[], source: ProjectHeadVO[]): void {
    if (ArrayUtils.isEmpty(source)) {
      return;
    }
    if (ArrayUtils.isEmpty(target)) {
      ArrayUtils.mergeArrays(target, source);
      return;
    }
    for (const sourceGroup of source) {
      const targetGroup: ProjectHeadVO = this.findProjectById(sourceGroup.id, target);
      if (!targetGroup) {
        target.push(sourceGroup);
      }
    }
  }

  private findProjectById(projectId: number, source: ProjectHeadVO[]): ProjectHeadVO {
    for (const targetProject of source) {
      if (targetProject.id === projectId) {
        return targetProject;
      }
    }
    return null;
  }

  private updateProjectGroupsByChangedSharedInfo(baseGroups: ProjectGroupVO[], filteredBaseGroups: ProjectGroupVO[]): void {
    for (const baseGroup of baseGroups) {
      if (this.groupIdToSharedInfos.containsKey(baseGroup.id)) {
        const sharedUserRight: SharedUserRightVO = this.groupIdToSharedInfos.getValue(baseGroup.id);
        if (sharedUserRight.isReadable && !sharedUserRight.isAutomaticApplied) {
          baseGroup.isSharedReadOnly = !sharedUserRight.isEditable;
        } else {
          baseGroup.isSharedReadOnly = null;
        }
      }
      this.filterProjectsBySharedRights(baseGroup, true);
      this.updateSharedRightsForChildren(baseGroup);
      if (filteredBaseGroups && !this.isGroupUnavailable(baseGroup)) {
        filteredBaseGroups.push(baseGroup);
      }
    }
  }

  private filterProjectsBySharedRights(projectGroup: ProjectGroupVO, loadedByFriendId: boolean): void {
    const projectHeads: ProjectHeadVO[] = projectGroup.projects;
    if (ArrayUtils.isEmpty(projectHeads)) {
      return;
    }
    const filteredProjects: ProjectVO[] = [];
    for (const projectHead of projectHeads) {
      let projectSharedUserRight: SharedUserRightVO = this.projectIdToSharedInfos.getValue(projectHead.id);
      if (loadedByFriendId) {
        // Apply local shared rights if they exist, or use 'isSharedReadOnly' flag.
        if (!projectSharedUserRight) {
          const parentSharedUserRight: SharedUserRightVO = this.getParentSharedUserRightByGroupVO(projectGroup);
          if (parentSharedUserRight && parentSharedUserRight.updateChildrenAvailable) {
            projectSharedUserRight = parentSharedUserRight;
          }
        }
        this.addProject(projectHead, projectSharedUserRight, filteredProjects);
      } else {
        if (!projectSharedUserRight) {
          const parentSharedUserRight: SharedUserRightVO = this.getParentSharedUserRightByGroupVO(projectGroup);
          if (parentSharedUserRight && parentSharedUserRight.updateChildrenAvailable) {
            // Apply shared rights of parent project group. 'updateChildrenAvailable' flag is true.
            if (parentSharedUserRight.isReadable) {
              this.addProject(projectHead, parentSharedUserRight, filteredProjects);
            }
          } else {
            // Shared rights of the project were checked at server side.
            this.addProject(projectHead, null, filteredProjects);
          }
        } else {
          // Apply local shared rights of the project.
          if (projectSharedUserRight.isReadable) {
            this.addProject(projectHead, projectSharedUserRight, filteredProjects);
          }
        }
      }
    }
    projectGroup.projects = filteredProjects;
  }

  private addProject(projectHead: ProjectHeadVO, sharedUserRight: SharedUserRightVO, projectHeads: ProjectHeadVO[]): void {
    if (sharedUserRight) {
      if (!sharedUserRight.isReadable) {
        return; // such project should not be added
      } else {
        projectHead.isSharedReadOnly = !sharedUserRight.isEditable;
      }
    }
    if (projectHead.isSharedReadOnly !== null) {
      projectHeads.push(projectHead);
    }
  }

  private getParentSharedUserRightByGroupVO(group: ProjectGroupVO): SharedUserRightVO {
    if (!group) {
      return null;
    }
    let result: SharedUserRightVO = null;
    if (this.groupIdToSharedInfos.containsKey(group.id)) {
      result = this.groupIdToSharedInfos.getValue(group.id);
      if (!result.isAutomaticApplied) {
        return result;
      }
    }
    if (group.parentGroup) {
      return this.getParentSharedUserRightByGroupVO(group.parentGroup);
    }
    return null;
  }

  private updateSharedRightsForChildren(projectGroup: ProjectGroupVO): void {
    for (const childGroup of projectGroup.groups.concat()) {
      this.updateSharedUserRightForProjectGroup(childGroup);
      this.filterProjectsBySharedRights(childGroup, true);
      this.updateSharedRightsForChildren(childGroup);
      if (this.isGroupUnavailable(childGroup)) {
        ArrayUtils.removeItem(childGroup, projectGroup.groups);
      }
    }
  }

  private isGroupUnavailable(projectGroup: ProjectGroupVO): Boolean {
    return ArrayUtils.isEmpty(projectGroup.groups) && ArrayUtils.isEmpty(projectGroup.projects) && projectGroup.isSharedReadOnly === null;
  }

  private updateSharedUserRightForProjectGroup(projectGroup: ProjectGroupVO): void {
    let sharedRight: SharedUserRightVO = null;
    if (this.groupIdToSharedInfos.containsKey(projectGroup.id)) {
      sharedRight = this.groupIdToSharedInfos.getValue(projectGroup.id);
    } else {
      const parentSharedUserRight: SharedUserRightVO = this.getParentSharedUserRightByGroupVO(projectGroup);
      sharedRight = new SharedUserRightVO();
      if (parentSharedUserRight && parentSharedUserRight.isAutomaticApplied) {
        sharedRight.isReadable = false;
        sharedRight.isEditable = false;
        sharedRight.updateChildrenAvailable = false;
      } else if (parentSharedUserRight && parentSharedUserRight.updateChildrenAvailable) {
        sharedRight.isReadable = parentSharedUserRight.isReadable;
        sharedRight.isEditable = parentSharedUserRight.isEditable;
        sharedRight.updateChildrenAvailable = parentSharedUserRight.updateChildrenAvailable;
      } else if (projectGroup.isSharedReadOnly === null) {
        sharedRight.isReadable = false;
        sharedRight.isEditable = false;
        sharedRight.updateChildrenAvailable = false;
      } else {
        sharedRight.isReadable = true;
        sharedRight.isEditable = !projectGroup.isSharedReadOnly;
        sharedRight.updateChildrenAvailable = false;
      }
      this.groupIdToSharedInfos.setValue(projectGroup.id, sharedRight);
    }
    if (!sharedRight.isReadable || sharedRight.isAutomaticApplied) {
      projectGroup.isSharedReadOnly = null;
    } else {
      projectGroup.isSharedReadOnly = !sharedRight.isEditable;
    }
  }

  private getLocallyChangedGroupNodes(notLoadedGroupsIds: number[]): ProjectGroupVO[] {
    const result: ProjectGroupVO[] = [];
    for (const notLoadedGroupId of notLoadedGroupsIds) {
      const group: ProjectGroupVO = this.buildLocalGroupTree(notLoadedGroupId);
      if (group) {
        result.push(group);
      }
    }
    return result;
  }

  private buildLocalGroupTree(groupId: number): ProjectGroupVO {
    if (!groupId) {
      return null;
    }

    const loadedGroup: ProjectGroupVO = this.findGroupById(groupId, this.ownerProjectGroups);
    if (loadedGroup) {
      const copiedGroup: ProjectGroupVO = CloneUtils.cloneProjectGroupVO(loadedGroup);
      // set rigths to default
      copiedGroup.isSharedReadOnly = null;
      for (const projectHead of copiedGroup.projects) {
        projectHead.isSharedReadOnly = null;
      }

      const copiedChildGroups: ProjectGroupVO[] = [];
      for (const childGroup of copiedGroup.groups) {
        const copiedChildGroup: ProjectGroupVO = this.buildLocalGroupTree(childGroup.id);
        if (copiedChildGroup) {
          copiedChildGroups.push(copiedChildGroup);
        } else {
          childGroup.isSharedReadOnly = null;
          copiedChildGroups.push(childGroup);
        }
        copiedGroup.groups = copiedChildGroups;
      }
      return copiedGroup;
    }
    return null;
  }

  public getEntryIcon(entry: ProjectGroupVO): SvgIconName {
    return ProjectGroupUtils.getProjectGroupIcon(entry, this.userModel.user);
  }
}
